import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import { IBingoBoard } from '@/interfaces/powerMarkets/bingo/IBingoBoard.ts';
import {
    IDashboard, DashboardOverrideName, IDashboardOverrides,
} from '@/interfaces/powerMarkets/dashboard/IDashboard.ts';
import { ILeaderboardUserOptions } from '@/interfaces/powerMarkets/settings/pages/ILeaderboardSettings.ts';
import { IMapPageSettingsData } from '@/interfaces/powerMarkets/settings/pages/IMapPageSettings.ts';
import { IPageData } from '@/interfaces/general/IPageData.ts';
import { ISiteSettings } from '@/interfaces/powerMarkets/settings/ISiteSettings.ts';
import { ISubscriptionSettings } from '@/interfaces/powerMarkets/notifications/ISubscriptionSettings.ts';
import {
    BoaLiveViews, IBoaCumulativeRemainingChartSettings, IBoaOnTableFilters, IUserSettings,
} from '@/interfaces/powerMarkets/settings/IUserSettings.ts';
import { IRemitSettings } from '@/interfaces/powerMarkets/settings/pages/IRemitSettings.ts';
import { userSettingsApi } from '@/api/powerMarkets/userSettingsApi.ts';
import { sendOtherTabUserSettingsChange } from '@/realTimeConnection/broadcastChannel.ts';
import {
    IChartModule,
    IForecastHistoryModule,
    IModule, isForecastHistoryModule, isModuleWithInfoProps, isModuleWithoutInfoProps,
} from '@/interfaces/powerMarkets/IModule.ts';
import { ISizePosition } from '@/interfaces/general/ISizePosition.ts';
import { IQuery } from '@/interfaces/powerMarkets/IQuery.ts';
import { cloneDeep } from '@/utils/global.ts';
import { IUserTableFiltersWithName } from '@/interfaces/powerMarkets/payloads/IUserTableFiltersWithName.ts';
import { CombinedOptions } from '@/interfaces/powerMarkets/balancing/IBoaMetricsEditor.ts';
import { IBoaAnalyticsOptions } from '@/interfaces/powerMarkets/balancing/IBoaAnalyticsOptions.ts';
import { getModuleType } from '@/utils/modules.ts';
import { LockedAccountEnum } from '@/enums/powerMarkets/LockedAccountEnum.ts';
import { normalizeSubscriptionSettings } from '@/utils/notifications/normalizeSubscriptionSettings.ts';
import { PeriodTypeOverrides } from '@/types/powerMarkets/balancing/PeriodTypeOverrides.ts';
import { WindowType } from '@/types/powerMarkets/balancing/WindowType.ts';
import { IBoaDashboardMapSettings } from '@/interfaces/powerMarkets/settings/pages/IBoaDashboardMapSettings.ts';
import { IMapLivePageSettings } from '@/interfaces/powerMarkets/settings/pages/IMapLivePageSettings.ts';
import { DEFAULT_USER_SETTINGS } from '@/constants/powerMarkets/defaultUserSettings.ts';
import { IDateObject } from '@/interfaces/powerMarkets/IDateObject.ts';
import { DEFAULT_PERIODS_PER_DAY, DEFAULT_TIMEZONE } from '@/constants/Time.ts';
import { IIndexGroup } from '@/interfaces/benchmarks/index/IIndexGroup.ts';
import { IGbIndexLegacyPageSettings } from '@/interfaces/benchmarks/index/IIndexLegacySettings.ts';
import { IBoaAcceptedTableSortOption } from '@/interfaces/powerMarkets/IBoaAcceptedTableSortOption.ts';
import { ILeaderboardGroup } from '@/interfaces/powerMarkets/leaderboard/ILeaderboardGroup.ts';
import { IOrderedSettings } from '@/interfaces/general/IOrderedSettings.ts';
import { TOrderedSettingsType } from '@/types/powerMarkets/TOrderedSettingsType.ts';
import { applyDashboardDateOverrideMirrors } from '@/helpers/powerMarkets/dashboardDateOverrideLegacySettings.ts';
import { applyPlantGroupSettingsWithLegacyMirrors } from '@/helpers/powerMarkets/plantGroupLegacySettings.ts';

type TOrderedSettingsData = IBingoBoard | IDashboard | ISubscriptionSettings | IIndexGroup | ILeaderboardGroup;

export const useUserSettingsStore = defineStore('userSettingsStore', () => {
    const userSettings = ref<IUserSettings>(DEFAULT_USER_SETTINGS);
    const userSettingsLoaded = ref<boolean>(false);
    const activeBingoId = ref<string | null>(null);
    const activeIndexGroupId = ref<string | null>(null);
    const activeLeaderboardId = ref<string | null>(null);
    const activeDashboardId = ref<string>('');
    const companyDashboards = ref<Array<IPageData<IDashboard>>>([]);
    const companyUsernames = ref<Array<string>>([]);

    function getBoaWindowTypeAll (): PeriodTypeOverrides {
        const type = userSettings.value.otherSettings.data.boaWindowType;

        if (typeof type === 'string') {
            return type;
        }
        if ('all' in type && type.all !== undefined) return type.all;
        return 'current';
    }

    function getBoaWindowTypeBoaDashboard (): PeriodTypeOverrides {
        return getPageOverrideBoaWindowType('boa');
    }

    function getBoaWindowTypeBoaComparer (): PeriodTypeOverrides {
        return getPageOverrideBoaWindowType('boaComparer');
    }

    function getBoaWindowTypeUserDashboard (): PeriodTypeOverrides {
        return getPageOverrideBoaWindowType('dashboard');
    }

    function getBoaWindowTypePlantActivity (): PeriodTypeOverrides {
        return getPageOverrideBoaWindowType('plantActivityBoard');
    }

    function getPageOverrideBoaWindowType (page: WindowType): PeriodTypeOverrides {
        const type = userSettings.value.otherSettings.data.boaWindowType;

        if (type[page]) return type[page];
        return getBoaWindowTypeAll();
    }

    function getPageDateOverrides (pageName: DashboardOverrideName): IDashboardOverrides | undefined {
        return userSettings.value.pageData.pageDates.data[pageName];
    }

    function getTimezoneForModule (moduleId: string): string {
        const module = Object.values(userSettings.value.dashboardsWithOrder.objectsToStore)
            .flatMap(dashboard => dashboard.data.modules)
            .find(mod => {
                if (isModuleWithInfoProps(mod)) {
                    return mod.info.id === moduleId;
                }
                return mod.id === moduleId;
            }) as IChartModule | undefined;

        return (module?.date as IDateObject).timezone ?? userSettings.value.otherSettings.data.timezone ?? DEFAULT_TIMEZONE;
    }

    function deleteCompanyDashboard (id: string): void {
        companyDashboards.value = companyDashboards.value.filter((i) => i.id !== id);
    }

    function getOrderedSettingsByType (type: TOrderedSettingsType): IOrderedSettings<TOrderedSettingsData> {
        switch (type) {
            case 'bingo':
                return userSettings.value.bingoWithOrder;
            case 'dashboard':
                return userSettings.value.dashboardsWithOrder;
            case 'notification':
                return userSettings.value.subscriptionsWithOrder;
            case 'indexGroup':
                return userSettings.value.indexGroupsWithOrder;
            case 'leaderboard':
                return userSettings.value.leaderboardsWithOrder;
            default:
                throw new Error(`Unknown ordered settings type: ${type}`);
        }
    }

    function deleteItemWithOrdering (
        id: string,
        type: TOrderedSettingsType,
        ordering: Array<string> | null,
    ): void {
        const itemsWithOrdering = getOrderedSettingsByType(type);
        delete itemsWithOrdering.objectsToStore[id];
        setOrderedSettingsOrder(itemsWithOrdering, ordering ?? itemsWithOrdering.order.data.filter(orderId => orderId !== id));
    }

    function deleteCurrentDashboardModule (moduleId: string): void {
        if (!userSettings.value.activeTab) return;
        const matchIndex = userSettings.value.dashboardsWithOrder.objectsToStore[userSettings.value.activeTab.data.id]?.data.modules.findIndex((module) => {
            if (isModuleWithInfoProps(module)) {
                return module.info.id === moduleId;
            }
            return module.id === moduleId;
        });
        if (matchIndex !== -1) {
            userSettings.value.dashboardsWithOrder.objectsToStore[userSettings.value.activeTab.data.id]?.data.modules.splice(matchIndex, 1);
        }
    }

    function updateCurrentDashboardModule (moduleId: string, newModule: IModule): void {
        if (!userSettings.value.activeTab) return;
        const matchIndex = userSettings.value.dashboardsWithOrder.objectsToStore[userSettings.value.activeTab.data.id]?.data.modules.findIndex((module) => {
            if (isModuleWithInfoProps(module)) {
                return module.info.id === moduleId;
            }
            return module.id === moduleId;
        });
        if (matchIndex !== -1) {
            userSettings.value.dashboardsWithOrder.objectsToStore[userSettings.value.activeTab.data.id].data.modules[matchIndex] = newModule;
        }
    }

    function updateCurrentDashboardModulesDate (moduleId: string, newModule: IModule): void {
        if (!userSettings.value.activeTab) return;
        const moduleType = getModuleType(newModule);
        userSettings.value.dashboardsWithOrder.objectsToStore[userSettings.value.activeTab.data.id].data.modules = userSettings.value.dashboardsWithOrder.objectsToStore[userSettings.value.activeTab.data.id].data.modules.map((module) => {
            if (isModuleWithInfoProps(module)) {
                if (module.info.id === moduleId) return module;
                if (isForecastHistoryModule(module) && isForecastHistoryModule(newModule)) {
                    return {
                        ...module,
                        data: {
                            ...module.data,
                            dateFormat: cloneDeep(newModule.data.dateFormat),
                        },
                    };
                }
                return module;
            }
            if (module.id === moduleId) return module;

            const localModuleType = getModuleType(module);
            if (moduleType === localModuleType) {
                if ('date' in module && 'date' in newModule) {
                    return {
                        ...module,
                        date: cloneDeep(newModule.date),
                    };
                }
            }
            return module;
        });
    }

    function updateCurrentDashboardModules (newModules: Array<IModule>): void {
        if (!userSettings.value.activeTab) return;
        userSettings.value.dashboardsWithOrder.objectsToStore[userSettings.value.activeTab.data.id].data.modules = newModules;
    }

    function setOrderedSettingsOrder<T> (itemsWithOrdering: IOrderedSettings<T>, ordering: Array<string>): void {
        itemsWithOrdering.order.data = ordering;
    }

    function setOrderedSettingsItem<T> (
        itemsWithOrdering: IOrderedSettings<T>,
        payload: IPageData<T>,
        ordering: Array<string> | null,
    ): void {
        itemsWithOrdering.objectsToStore[payload.id] = {
            data: payload.data,
            id: payload.id,
        };

        if (ordering) {
            setOrderedSettingsOrder(itemsWithOrdering, ordering);
        }
    }

    function setBingoItemsWithOrdering (payload: IPageData<IBingoBoard>, ordering: Array<string> | null): void {
        setOrderedSettingsItem(userSettings.value.bingoWithOrder, payload, ordering);
    }

    function setCompanyDashboards (dashboards: Array<IPageData<IDashboard>>): void {
        companyDashboards.value = dashboards;
    }

    function setCompanyUsers (users: Array<string>): void {
        companyUsernames.value = users;
    }

    function setDashboardOrdering (ordering: Array<string>): void {
        setOrderedSettingsOrder(userSettings.value.dashboardsWithOrder, ordering);
    }

    function setIndexGroupOrdering (ordering: Array<string>): void {
        setOrderedSettingsOrder(userSettings.value.indexGroupsWithOrder, ordering);
    }

    function getIndexGroupById (id: string): IIndexGroup | null {
        return userSettings.value.indexGroupsWithOrder.objectsToStore[id]?.data ?? null;
    }

    function getLeaderboardGroupById (id: string): ILeaderboardGroup | null {
        return userSettings.value.leaderboardsWithOrder.objectsToStore[id]?.data ?? null;
    }

    function setIndexGroupWithOrdering (payload: IPageData<IIndexGroup>, indexGroupIdOrder: Array<string> | null): void {
        setOrderedSettingsItem(userSettings.value.indexGroupsWithOrder, payload, indexGroupIdOrder);
    }

    function setLeaderboardGroupOrdering (ordering: Array<string>): void {
        setOrderedSettingsOrder(userSettings.value.leaderboardsWithOrder, ordering);
    }

    function setLeaderboardGroupWithOrdering (payload: IPageData<ILeaderboardGroup>, leaderboardGroupIdOrder: Array<string> | null): void {
        setOrderedSettingsItem(userSettings.value.leaderboardsWithOrder, payload, leaderboardGroupIdOrder);
    }

    function setDashboardItemsWithOrdering (payload: IPageData<IDashboard>, ordering: Array<string> | null): void {
        setOrderedSettingsItem(userSettings.value.dashboardsWithOrder, payload, ordering);
    }

    function setMapSettings (value: IMapPageSettingsData): void {
        userSettings.value.pageData.map.data = value;
    }

    function setBingoMapSettings (value: IMapLivePageSettings): void {
        userSettings.value.pageData.mapLive.data = value;
    }

    function setBoaDashboardMapSettings (value: IBoaDashboardMapSettings): void {
        userSettings.value.pageData.boaDashboardMap.data = value;
    }

    function setUserTableFilters (value: IUserTableFiltersWithName): void {
        if (value.filters === null) {
            delete userSettings.value.tableFilters[value.name];
            return;
        }
        if (!userSettings.value.tableFilters[value.name]) {
            userSettings.value.tableFilters[value.name] = { id: '', data: value.filters };
        } else {
            userSettings.value.tableFilters[value.name].data = value.filters;
        }
    }

    function setSubscriptionSettings (value: ISubscriptionSettings): void {
        // TEMP dual-run: normalizeSubscriptionSettings (nbd→ndb + null limiters).
        // Delete once old site is gone — see normalizeSubscriptionSettings.ts.
        const normalised = normalizeSubscriptionSettings(value);

        if (!userSettings.value.subscriptionsWithOrder.order.data.includes(normalised.uiId)) {
            userSettings.value.subscriptionsWithOrder.order.data.unshift(normalised.uiId);
        }

        userSettings.value.subscriptionsWithOrder.objectsToStore[normalised.uiId] = {
            id: '',
            data: normalised,
        };
    }

    function setUserSettings (value: IUserSettings): void {
        userSettings.value = { ...value };
        activeBingoId.value = value.bingoActiveTab?.data ?? null;
        activeIndexGroupId.value = value.indexGroupActiveTab?.data ?? null;
        activeLeaderboardId.value = value.leaderboardActiveTab?.data ?? null;

        // TEMP dual-site: hydrate dateOverrides.chart from Enact fromToDateOverride
        const dashboards = userSettings.value.dashboardsWithOrder?.objectsToStore;
        if (dashboards) {
            Object.values(dashboards).forEach((item) => {
                if (item?.data) applyDashboardDateOverrideMirrors(item.data, { hydrateFromLegacy: true });
            });
        }
    }

    function updateActiveIndexGroupId (value: string): void {
        activeIndexGroupId.value = value;
    }

    function updateActiveBingoId (value: string): void {
        activeBingoId.value = value;
    }

    function updateActiveLeaderboardId (value: string | null): void {
        activeLeaderboardId.value = value;
    }

    function updateActiveDashboard (value: IDashboard | null): void {
        if (!value) {
            userSettings.value.activeTab = null;
            return;
        }

        activeDashboardId.value = value?.id ?? '';
        if (!userSettings.value || !value) return;

        userSettings.value.activeTab = {
            ...userSettings.value.activeTab,
            data: value,
            id: value.id,
        };
    }

    function addModuleToActiveDashboard (module: IModule): void {
        if (!userSettings.value.activeTab?.data) return;
        userSettings.value.dashboardsWithOrder.objectsToStore[userSettings.value.activeTab.data.id]?.data.modules.push(module);
    }

    function cloneModuleToDashboards (module: IModule, dashboardIds: Array<string>): { moduleIdOfClonedInCurrentDashboard: string; newSizeAndPositionClonedInCurrentDashboard: ISizePosition } {
        const ogSizeAndPosition = isModuleWithInfoProps(module) ? module.info.sizeAndPosition : module.sizeAndPosition;
        const newSizeAndPosition: ISizePosition = {
            x: 'auto',
            y: 'auto',
            w: ogSizeAndPosition?.w ?? 4,
            h: ogSizeAndPosition?.h ?? 4,
        };

        const clonedInCurrentDash: { moduleIdOfClonedInCurrentDashboard: string; newSizeAndPositionClonedInCurrentDashboard: ISizePosition } = {
            moduleIdOfClonedInCurrentDashboard: '',
            newSizeAndPositionClonedInCurrentDashboard: newSizeAndPosition,
        };

        dashboardIds.forEach((dashboardId) => {
            const newModule = cloneDeep(module);
            let newModuleId = '';
            if (isModuleWithInfoProps(newModule)) {
                newModule.info.sizeAndPosition = newSizeAndPosition;
                newModule.info.id = crypto.randomUUID();
                newModuleId = newModule.info.id;
            } else {
                newModule.sizeAndPosition = newSizeAndPosition;
                newModule.id = crypto.randomUUID();
                newModuleId = newModule.id;
            }

            if (dashboardId === userSettings.value.activeTab?.data.id) {
                clonedInCurrentDash.moduleIdOfClonedInCurrentDashboard = newModuleId;
            }

            userSettings.value.dashboardsWithOrder.objectsToStore[dashboardId]?.data.modules.push(newModule);
        });

        return clonedInCurrentDash;
    }

    function updateModuleSizeAndPosition (moduleId: string, sizeAndPosition: ISizePosition): void {
        if (!userSettings.value.activeTab?.data) return;
        if (!userSettings.value.dashboardsWithOrder.objectsToStore[userSettings.value.activeTab.data.id]?.data.modules) return;
        userSettings.value.dashboardsWithOrder.objectsToStore[userSettings.value.activeTab.data.id].data.modules = userSettings.value.dashboardsWithOrder.objectsToStore[userSettings.value.activeTab.data.id].data.modules.map(module => {
            const moduleModified = { ...module };
            if (isModuleWithInfoProps(moduleModified) && moduleModified.info.id === moduleId) {
                moduleModified.info.sizeAndPosition = sizeAndPosition;
            } else if (isModuleWithoutInfoProps(moduleModified) && moduleModified.id === moduleId) {
                moduleModified.sizeAndPosition = sizeAndPosition;
            }
            return moduleModified;
        });
    }

    function updateUserCountry (val: string): void {
        userSettings.value.otherSettings.data.country = val;
    }

    async function setPageDatesSettingSingle (prop: DashboardOverrideName, newSetting: IDashboardOverrides): Promise<void> {
        userSettings.value.pageData.pageDates.data[prop] = newSetting;
        if (userSettings.value.otherSettings.data.lockedAccount === LockedAccountEnum.UploadLocked) return;
        // TODO this needs testing, as Shiv doesn't understand how it works
        await userSettingsApi.postPageSettings({ pageName: 'pageDates', data: userSettings.value.pageData.pageDates.data });
    }

    function setGbIndexSettings (value: IGbIndexLegacyPageSettings): void {
        userSettings.value.pageData.plantIndex.data = value;
    }

    function setLeaderboardSettings (value: ILeaderboardUserOptions): void {
        userSettings.value.pageData.bmLeaderboard.data.userOptions = value;
    }

    function setRemitSettings (value: IRemitSettings): void {
        userSettings.value.pageData.remit.data = value;
    }

    function setUserDashboardSettings (value: IDashboard, dashboardId: string): void {
        userSettings.value.dashboardsWithOrder.objectsToStore[dashboardId].data = value;
        if (userSettings.value.dashboardsWithOrder.order.data.includes(dashboardId)) return;
        userSettings.value.dashboardsWithOrder.order.data.unshift(dashboardId);
    }

    async function setLeaderboardSettingSingle<K extends keyof ILeaderboardUserOptions> (prop: K, newSetting: ILeaderboardUserOptions[K], postUpdate?: boolean): Promise<void> {
        userSettings.value.pageData.bmLeaderboard.data.userOptions[prop] = newSetting;

        if (!postUpdate) return;
        if (userSettings.value.otherSettings.data.lockedAccount === LockedAccountEnum.UploadLocked) return;
        await userSettingsApi.postPageSettings({ pageName: 'bmLeaderboard', data: { userOptions: userSettings.value.pageData.bmLeaderboard.data.userOptions } });
    }

    async function setLeaderboardSettingMultiple<K extends keyof ILeaderboardUserOptions> (
        settings: Array<[K, ILeaderboardUserOptions[K]]>, // Array of tuples with key-value pairs
        postUpdate?: boolean,
    ): Promise<void> {
        settings.forEach(([prop, newSetting]) => {
            userSettings.value.pageData.bmLeaderboard.data.userOptions[prop] = newSetting;
        });

        if (!postUpdate) return;
        if (userSettings.value.otherSettings.data.lockedAccount === LockedAccountEnum.UploadLocked) return;

        await userSettingsApi
            .postPageSettings({
                pageName: 'bmLeaderboard',
                data: { userOptions: userSettings.value.pageData.bmLeaderboard.data.userOptions },
            });
    }

    async function setHistoricQueries (queries: Array<IQuery>): Promise<void> {
        userSettings.value.pageData.historicQueries.data = queries;
        if (userSettings.value.otherSettings.data.lockedAccount === LockedAccountEnum.UploadLocked) return;

        await userSettingsApi.postPageSettings({ pageName: 'historicQueries', data: queries });
    }

    function setSiteSettings<K extends keyof ISiteSettings> (prop: K, newSetting: ISiteSettings[K], postUpdate = false): void {
        userSettings.value.otherSettings.data[prop] = newSetting;

        // TEMP dual-site: chosenPlantGroupColour falls back to defaultPlantGroupColour in
        // buildLegacyPlantGroupMirrors — refresh mirrors when that default changes outside plant-group saves.
        if (prop === 'defaultPlantGroupColour') {
            applyPlantGroupSettingsWithLegacyMirrors(userSettings.value.otherSettings.data, {});
        }

        if (!postUpdate) return;
        if (prop !== 'lockedAccount' && userSettings.value.otherSettings.data.lockedAccount === LockedAccountEnum.UploadLocked) return;

        userSettingsApi.postUserSettings(userSettings.value.otherSettings.data).then(() => sendOtherTabUserSettingsChange());
    }

    function setSitewideTimezone (newTimezone: string): void {
        setSiteSettings('timezone', newTimezone, true);
    }
    function setSitewidePeriodsPerDay (newPeriodsPerDay: number): void {
        setSiteSettings('periodsInDayForCharts', newPeriodsPerDay, true);
    }

    function setBoaAcceptedTableSortOptions (key: string, newSetting: IBoaAcceptedTableSortOption, postUpdate?: boolean): void {
        if (!userSettings.value.otherSettings.data.boaAcceptedTableSortOptions) userSettings.value.otherSettings.data.boaAcceptedTableSortOptions = {};
        userSettings.value.otherSettings.data.boaAcceptedTableSortOptions[key] = newSetting;
        userSettings.value.otherSettings.data.boaAcceptedTableSortOption = {
            metric: newSetting.field ?? newSetting.metric,
            order: newSetting.order,
        };

        if (!postUpdate) return;
        if (userSettings.value.otherSettings.data.lockedAccount === LockedAccountEnum.UploadLocked) return;

        userSettingsApi.postUserSettings(userSettings.value.otherSettings.data).then(() => sendOtherTabUserSettingsChange());
    }

    async function setBoaFilterOptions (options: CombinedOptions): Promise<void> {
        userSettings.value.pageData.boa.data.boaFilterOptions = options;
        if (userSettings.value.otherSettings.data.lockedAccount === LockedAccountEnum.UploadLocked) return;

        await userSettingsApi.postPageSettings({ pageName: 'boa', data: userSettings.value.pageData.boa.data });
    }

    async function updateBoaLiveViewMode (newMode: BoaLiveViews): Promise<void> {
        userSettings.value.pageData.boa.data.currentView = newMode;
        if (userSettings.value.otherSettings.data.lockedAccount === LockedAccountEnum.UploadLocked) return;

        await userSettingsApi.postPageSettings({ pageName: 'boa', data: userSettings.value.pageData.boa.data });
    }

    async function setBoaAnalyticsOptions (options: IBoaAnalyticsOptions): Promise<void> {
        userSettings.value.pageData.boa.data.analyticsOptions = options;
        if (userSettings.value.otherSettings.data.lockedAccount === LockedAccountEnum.UploadLocked) return;

        await userSettingsApi.postPageSettings({ pageName: 'boa', data: userSettings.value.pageData.boa.data });
    }

    async function setBoaCumulativeRemainingChartSettings (options: IBoaCumulativeRemainingChartSettings): Promise<void> {
        const boaData = userSettings.value.pageData.boa.data;
        boaData.chartBidOfferToShow = options.chartBidOfferToShow;
        boaData.chartBidOnOffUpDown = options.chartBidOnOffUpDown;
        boaData.chartNoticeToDeviateFilter = options.chartNoticeToDeviateFilter;
        boaData.chartMaxPrice = options.chartMaxPrice;
        boaData.chartHideUnwind = options.chartHideUnwind;
        boaData.chartHideWind = options.chartHideWind;

        if (userSettings.value.otherSettings.data.lockedAccount === LockedAccountEnum.UploadLocked) return;

        await userSettingsApi.postPageSettings({ pageName: 'boa', data: userSettings.value.pageData.boa.data });
    }

    async function setBoaOnTableFilters (filters: IBoaOnTableFilters, isOffer: boolean): Promise<void> {
        if (isOffer) {
            userSettings.value.pageData.boa.data.offerOnTableFilters = filters;
        } else {
            userSettings.value.pageData.boa.data.bidOnTableFilters = filters;
        }
        if (userSettings.value.otherSettings.data.lockedAccount === LockedAccountEnum.UploadLocked) return;

        await userSettingsApi.postPageSettings({ pageName: 'boa', data: userSettings.value.pageData.boa.data });
    }

    async function setBoaComparerTableFilters (filters: { bids?: Record<string, unknown>; offers?: Record<string, unknown> }): Promise<void> {
        const current = userSettings.value.pageData.boa.data.boaComparerTableFilters ?? { bids: {}, offers: {} };
        userSettings.value.pageData.boa.data.boaComparerTableFilters = {
            bids: filters.bids ?? current.bids,
            offers: filters.offers ?? current.offers,
        };
        if (userSettings.value.otherSettings.data.lockedAccount === LockedAccountEnum.UploadLocked) return;

        await userSettingsApi.postPageSettings({ pageName: 'boa', data: userSettings.value.pageData.boa.data });
    }

    const userCountry = computed(() => {
        const useFallback = userSettings.value.otherSettings.data.country === 'None';
        return useFallback ? userSettings.value.otherSettings.data.fallbackCountry : userSettings.value.otherSettings.data.country;
    });

    const sitewideTimezone = computed<string>(() => userSettings.value.otherSettings.data?.timezone ?? DEFAULT_TIMEZONE);

    const sitewidePeriodsPerDay = computed<number>(() => userSettings.value.otherSettings.data?.periodsInDayForCharts ?? DEFAULT_PERIODS_PER_DAY);

    const pageChartTimezoneOverride = (viewKey?: number): string | undefined => {
        if (viewKey === undefined || viewKey === -1) return undefined;
        const dashboardId = userSettings.value.dashboardsWithOrder.order.data[viewKey];
        if (!dashboardId) return undefined;
        const dashboard = userSettings.value.dashboardsWithOrder.objectsToStore?.[dashboardId]?.data;
        const chartOverrides = dashboard?.dateOverrides?.chart;
        return chartOverrides?.timezone ?? undefined;
    };

    const pageChartPeriodsPerDayOverride = (viewKey?: number): number | undefined => {
        if (viewKey === undefined || viewKey === -1) return undefined;
        const dashboardId = userSettings.value.dashboardsWithOrder.order.data[viewKey];
        if (!dashboardId) return undefined;
        const dashboard = userSettings.value.dashboardsWithOrder.objectsToStore?.[dashboardId]?.data;
        const chartOverrides = dashboard?.dateOverrides?.chart;
        return chartOverrides?.periodsPerDay ?? undefined;
    };

    const moduleTimezone = (viewKey?: number, moduleIndex?: number): string | undefined => {
        if (viewKey === undefined || viewKey === -1 || moduleIndex === undefined || moduleIndex === -1) return undefined;
        const dashboardId = userSettings.value.dashboardsWithOrder.order.data[viewKey];
        if (!dashboardId) return undefined;
        const module = userSettings.value.dashboardsWithOrder.objectsToStore?.[dashboardId]?.data?.modules[moduleIndex];
        return ((module as IChartModule)?.date as IDateObject)?.timezone ?? (module as IForecastHistoryModule)?.data?.dateFormat?.timezone ?? undefined;
    };

    const modulePeriodsPerDay = (viewKey?: number, moduleIndex?: number): number | undefined => {
        if (viewKey === undefined || viewKey === -1 || moduleIndex === undefined || moduleIndex === -1) return undefined;
        const dashboardId = userSettings.value.dashboardsWithOrder.order.data[viewKey];
        if (!dashboardId) return undefined;
        const module = userSettings.value.dashboardsWithOrder.objectsToStore?.[dashboardId]?.data?.modules[moduleIndex];
        return ((module as IChartModule)?.date as IDateObject)?.periodsPerDay ?? (module as IForecastHistoryModule)?.data?.dateFormat?.periodsPerDay ?? undefined;
    };

    function findModuleById (module: IModule, id: string): boolean {
        if (isModuleWithInfoProps(module)) {
            return module.info.id === id;
        }
        return module.id === id;
    }

    function updateDashboardTimezoneAndPeriodFrequency (
        dashboardId: string,
        options: { timezoneId?: string | null; periodsPerDay?: number | null; moduleId?: string },
    ): void {
        if (!dashboardId) return;
        const dashboard = userSettings.value.dashboardsWithOrder.objectsToStore?.[dashboardId]?.data;
        if (!dashboard) return;

        const { timezoneId, periodsPerDay, moduleId } = options;

        if (moduleId) {
            const module = dashboard.modules.find(mod => findModuleById(mod, moduleId));

            if (!module) return;

            const moduleUpdates: Partial<IDateObject> = {
                timezone: timezoneId,
                periodsPerDay,
            };

            const payloadData: IDashboard = {
                ...dashboard,
                modules: [
                    ...dashboard.modules.filter(mod => !findModuleById(mod, moduleId)),
                    {
                        ...module,
                        date: {
                            ...(module as IChartModule).date as IDateObject,
                            ...moduleUpdates,
                        },
                    },
                ],
            };

            userSettings.value.dashboardsWithOrder.objectsToStore[dashboardId].data = payloadData;

            userSettingsApi.postItemWithOrdering({
                id: dashboardId,
                ordering: null,
                data: payloadData,
                type: 'dashboard',
            });
        } else {
            if (!dashboard.dateOverrides) {
                dashboard.dateOverrides = {};
            }
            const payloadData: IDashboard = applyDashboardDateOverrideMirrors({
                ...dashboard,
                dateOverrides: {
                    ...dashboard.dateOverrides,
                    chart: {
                        ...dashboard.dateOverrides.chart,
                        timezone: timezoneId ?? null,
                        periodsPerDay: periodsPerDay ?? null,
                    },
                },
            });

            userSettings.value.dashboardsWithOrder.objectsToStore[dashboardId].data = payloadData;

            userSettingsApi.postItemWithOrdering({
                id: dashboardId,
                ordering: null,
                data: payloadData,
                type: 'dashboard',
            });
        }
    }

    async function updatePageFromToDateOverride (payload?: { fromTo?: IDashboardOverrides; viewKey?: number }): Promise<void> {
        if (payload?.viewKey === undefined || payload.viewKey === -1) return;
        const dashboardId = userSettings.value.dashboardsWithOrder.order.data[payload.viewKey];
        if (!dashboardId) return;

        const dashboard = userSettings.value.dashboardsWithOrder.objectsToStore?.[dashboardId]?.data;
        if (!dashboard) return;

        if (payload?.fromTo) {
            dashboard.dateOverrides = payload.fromTo;
        }

        applyDashboardDateOverrideMirrors(dashboard);

        if (userSettings.value.otherSettings.data.lockedAccount === LockedAccountEnum.UploadLocked) return;

        const pageName = 'userDashboard';
        await userSettingsApi.postPageSettings({
            pageName,
            data: dashboard,
        });
    }

    return {
        activeBingoId,
        activeIndexGroupId,
        activeLeaderboardId,
        companyDashboards,
        companyUsernames,
        sitewidePeriodsPerDay,
        sitewideTimezone,
        userCountry,
        userSettings,
        userSettingsLoaded,
        getBoaWindowTypeAll,
        getBoaWindowTypeBoaDashboard,
        getBoaWindowTypeBoaComparer,
        getBoaWindowTypeUserDashboard,
        getBoaWindowTypePlantActivity,
        getPageDateOverrides,
        deleteCompanyDashboard,
        deleteItemWithOrdering,
        setBingoItemsWithOrdering,
        setCompanyDashboards,
        setCompanyUsers,
        setIndexGroupOrdering,
        getIndexGroupById,
        getLeaderboardGroupById,
        setIndexGroupWithOrdering,
        setLeaderboardGroupOrdering,
        setLeaderboardGroupWithOrdering,
        setDashboardItemsWithOrdering,
        setDashboardOrdering,
        setUserTableFilters,
        setPageDatesSettingSingle,
        setGbIndexSettings,
        setLeaderboardSettings,
        setLeaderboardSettingSingle,
        setLeaderboardSettingMultiple,
        setHistoricQueries,
        setMapSettings,
        setBingoMapSettings,
        setBoaDashboardMapSettings,
        setSiteSettings,
        setSubscriptionSettings,
        setUserSettings,
        setUserDashboardSettings,
        updateActiveBingoId,
        updateActiveDashboard,
        updateActiveIndexGroupId,
        updateActiveLeaderboardId,
        addModuleToActiveDashboard,
        updateModuleSizeAndPosition,
        updateDashboardTimezoneAndPeriodFrequency,
        setSitewideTimezone,
        setSitewidePeriodsPerDay,
        updateUserCountry,
        deleteCurrentDashboardModule,
        updateCurrentDashboardModule,
        updateCurrentDashboardModules,
        updateCurrentDashboardModulesDate,
        cloneModuleToDashboards,
        setBoaFilterOptions,
        setBoaAnalyticsOptions,
        setBoaCumulativeRemainingChartSettings,
        setBoaOnTableFilters,
        setBoaComparerTableFilters,
        setRemitSettings,
        updateBoaLiveViewMode,
        pageChartTimezoneOverride,
        pageChartPeriodsPerDayOverride,
        moduleTimezone,
        modulePeriodsPerDay,
        updatePageFromToDateOverride,
        getTimezoneForModule,
        setBoaAcceptedTableSortOptions,
    };
});
